/**
 * Reports she runs herself.
 *
 * Both of these existed as API endpoints before they existed as buttons — the board paper had
 * no caller at all, and the Excel export was reachable only from a corner of the log view. A
 * capability nobody can find is a capability nobody has, so this is the tab where she runs
 * them, on the months and clients she chooses, without waiting for a schedule or for Craig.
 *
 * **Preview first, generate second**, and that order is the whole design. A board paper takes
 * a model call to draft its executive summary and comes back as a .docx she then has to open
 * to find out what is in it. The preview uses exactly the same selection and costs nothing, so
 * she can see whether a month is worth a document before making one — including the answer
 * that matters most often, which is that the month was quiet and there is nothing to send.
 *
 * Nothing here is emailed anywhere. She downloads it, edits it, and decides who sees it.
 */

import { useCallback, useEffect, useState } from "react";

import { api } from "../api";
import { AssessNow } from "../components/AssessNow";
import { GeneratedDocuments } from "../components/GeneratedDocuments";
import { RunsInProgress } from "../components/RunsInProgress";
import { HandbookSearchBox } from "../components/HandbookLookup";
import type { BoardPaperPreview, Client, Item, PaperLine } from "../types";

/** The last twelve months, newest first — the realistic range for a monthly paper. */
function recentMonths(count = 12): string[] {
  const now = new Date();
  return Array.from({ length: count }, (_, offset) => {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  });
}

function monthLabel(key: string): string {
  const [year, month] = key.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

function Line({ line }: { line: PaperLine }) {
  return (
    <li>
      <span className="line-title">
        {line.reference && <code>{line.reference}</code>} {line.title}
      </span>
      {line.note && (
        <span className={line.note_is_hers ? "note hers" : "note ai"}>
          {line.note}
          {!line.note_is_hers && <em> — AI wording, not yours</em>}
        </span>
      )}
      {line.next_steps && <span className="muted small">Next: {line.next_steps}</span>}
      {line.due_date && <span className="muted small">Due {line.due_date}</span>}
    </li>
  );
}

function Preview({ preview }: { preview: BoardPaperPreview }) {
  if (preview.is_empty) {
    return (
      <div className="preview empty-preview">
        <p>
          <strong>Nothing to report for {monthLabel(preview.month)}.</strong>
        </p>
        <p className="muted small">
          No tracked developments for {preview.client} that month, no open actions and nothing
          on the horizon. A paper would say so in two pages — which is a real answer, and
          sometimes the right one to send, but worth knowing before you make it.
        </p>
      </div>
    );
  }

  const aiNotes = [
    ...preview.sections.flatMap((s) => s.items),
    ...preview.open_actions,
    ...preview.horizon,
  ].filter((line) => line.note && !line.note_is_hers).length;

  return (
    <div className="preview">
      <p className="preview-count">
        <strong>{preview.item_count}</strong> development
        {preview.item_count === 1 ? "" : "s"}
        {preview.open_actions.length > 0 && <>, {preview.open_actions.length} open action(s)</>}
        {preview.horizon.length > 0 && <>, {preview.horizon.length} on the horizon</>}
      </p>

      {aiNotes > 0 && (
        <p className="small ai-warning">
          {aiNotes} relevance note{aiNotes === 1 ? " is" : "s are"} the model's wording rather
          than yours. Her voice is what the client is paying for — worth a look before this
          goes out.
        </p>
      )}

      {preview.sections.map((section) => (
        <section key={section.regulator}>
          <h4>{section.regulator}</h4>
          <ul className="paper-lines">
            {section.items.map((line, index) => (
              <Line key={`${line.url}-${index}`} line={line} />
            ))}
          </ul>
        </section>
      ))}

      {preview.open_actions.length > 0 && (
        <section>
          <h4>Actions</h4>
          <ul className="paper-lines">
            {preview.open_actions.map((line, index) => (
              <Line key={`action-${index}`} line={line} />
            ))}
          </ul>
        </section>
      )}

      {preview.horizon.length > 0 && (
        <section>
          <h4>Horizon</h4>
          <ul className="paper-lines">
            {preview.horizon.map((line, index) => (
              <Line key={`horizon-${index}`} line={line} />
            ))}
          </ul>
        </section>
      )}

      <p className="small muted">
        The executive summary is not shown: it is the one part that costs a model call, so it is
        drafted when the document is made. It arrives marked as a draft.
      </p>
    </div>
  );
}

export function ReportsView({
  clients,
  onError,
}: {
  clients: Client[];
  onError: (error: unknown) => void;
}) {
  const months = recentMonths();
  const live = clients.filter((client) => client.status !== "archived");

  const [client, setClient] = useState(live[0]?.name ?? "");
  const [month, setMonth] = useState(months[1] ?? months[0]);
  const [preview, setPreview] = useState<BoardPaperPreview | null>(null);
  const [busy, setBusy] = useState<"" | "preview" | "paper" | "excel">("");
  const [ready, setReady] = useState<{ url: string; filename: string } | null>(null);

  // Excel export gets its own filters: she exports the whole log by month far more often
  // than she exports one client, and tying it to the paper's selection would be tidier code
  // and a worse tool.
  const [exportMonth, setExportMonth] = useState("");
  const [exportClient, setExportClient] = useState("");
  const [exportReady, setExportReady] = useState<{ url: string; filename: string } | null>(null);
  /** Bumped when an assessment finishes, so the documents list re-reads the bucket. */
  const [documentsVersion, setDocumentsVersion] = useState(0);
  const documentsChanged = useCallback(() => setDocumentsVersion((n) => n + 1), []);
  /** Bumped when a run is started from the Assess card, so In progress shows it straight away. */
  const [runsVersion, setRunsVersion] = useState(0);
  const runsChanged = useCallback(() => setRunsVersion((n) => n + 1), []);

  // The impact-assessment picker's choices. Tracked items only: an assessment of something she
  // has not decided to track is work nobody asked for.
  //
  // `listAllItems`, not a single big page: the API clamps `limit` to 200 without complaining,
  // and DynamoDB filters *after* reading a page — so one request would silently offer her a
  // subset of her tracked items with nothing to say some were missing.
  const [items, setItems] = useState<Item[]>([]);

  useEffect(() => {
    let live = true;
    api
      .listAllItems({ status: "tracked" })
      .then((tracked) => {
        if (live) setItems(tracked);
      })
      .catch(onError);
    return () => {
      live = false;
    };
  }, [onError]);

  const runPreview = async () => {
    setBusy("preview");
    setPreview(null);
    setReady(null);
    try {
      setPreview(await api.previewBoardPaper(client, month));
    } catch (error) {
      onError(error);
    } finally {
      setBusy("");
    }
  };

  const runPaper = async () => {
    setBusy("paper");
    try {
      const result = await api.createBoardPaper(client, month);
      setReady({ url: result.url, filename: result.filename });
    } catch (error) {
      onError(error);
    } finally {
      setBusy("");
    }
  };

  const runExport = async () => {
    setBusy("excel");
    setExportReady(null);
    try {
      const result = await api.createExport({
        status: "tracked",
        month: exportMonth || undefined,
        client: exportClient || undefined,
      });
      setExportReady({ url: result.url, filename: result.filename });
    } catch (error) {
      onError(error);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="reports">
      <div className="card">
        <h3>Board paper</h3>
        <p className="muted small">
          One client, one month, as a Word document you can edit. Preview it first — that costs
          nothing and tells you whether the month is worth a paper.
        </p>

        <div className="row report-controls">
          <label>
            Client
            <select value={client} onChange={(event) => setClient(event.target.value)}>
              {live.map((entry) => (
                <option key={entry.id} value={entry.name}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Month
            <select value={month} onChange={(event) => setMonth(event.target.value)}>
              {months.map((key) => (
                <option key={key} value={key}>
                  {monthLabel(key)}
                </option>
              ))}
            </select>
          </label>
          <button disabled={!client || !!busy} onClick={runPreview}>
            {busy === "preview" ? "Reading…" : "Preview"}
          </button>
          <button className="primary" disabled={!client || !!busy} onClick={runPaper}>
            {busy === "paper" ? "Writing…" : "Generate Word document"}
          </button>
        </div>

        {ready && (
          <p className="ready">
            <a href={ready.url} target="_blank" rel="noreferrer noopener">
              Download {ready.filename}
            </a>{" "}
            <span className="muted small">
              — a draft, watermarked as one. The link expires shortly; generate it again
              whenever you need it.
            </span>
          </p>
        )}

        {preview && <Preview preview={preview} />}
      </div>

      <div className="card">
        <h3>Excel export</h3>
        <p className="muted small">
          Your tracked log, in the workbook layout you already use. Leave both boxes empty for
          everything.
        </p>

        <div className="row report-controls">
          <label>
            Month
            <select value={exportMonth} onChange={(event) => setExportMonth(event.target.value)}>
              <option value="">every month</option>
              {months.map((key) => (
                <option key={key} value={key}>
                  {monthLabel(key)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Client
            <select
              value={exportClient}
              onChange={(event) => setExportClient(event.target.value)}
            >
              <option value="">every client</option>
              {live.map((entry) => (
                <option key={entry.id} value={entry.name}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
          <button className="primary" disabled={!!busy} onClick={runExport}>
            {busy === "excel" ? "Building…" : "Export to Excel"}
          </button>
        </div>

        {exportReady && (
          <p className="ready">
            <a href={exportReady.url} target="_blank" rel="noreferrer noopener">
              Download {exportReady.filename}
            </a>
          </p>
        )}
      </div>

      <div className="card">
        <h3>Look up a rule</h3>
        <p className="muted small">
          Search the Handbook by keyword or reference. Results open the FCA's own page in a window
          beside this one — sized to half the screen, so you can read the rule and your own work
          together. The same search sits
          on the Log screen, and any reference in your log is already a link.
        </p>
        <HandbookSearchBox />
      </div>

      <AssessNow
        items={items}
        onError={onError}
        onFinished={documentsChanged}
        onStarted={runsChanged}
      />

      <RunsInProgress onError={onError} refresh={runsVersion} onFinished={documentsChanged} />

      <GeneratedDocuments onError={onError} refresh={documentsVersion} />
    </div>
  );
}
